import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import {
	RootStore,
	UserStore,
	BookingDictionariesStore,
	AddressesStore,
	CCStore,
	BookingCCStore,
} from '../../stores';
import styles from './styles.module.scss';
import { Form, Input, InputPassword, Select, TextArea } from 'components/antd';
import {
	PhoneInput,
	AlternativeContacts,
	LocationInput,
	NotificationByEmail,
	ReferenceFields,
} from 'components';
import { CreditCards } from 'components';
import { FormComponentProps } from 'antd/lib/form';
import { toJS } from 'mobx';
import { FORM_STRUCTURE } from 'AppClient/api/UserProfile';
import { getOptionalLabel } from 'components/antd/Modal/TitleNotice';
import { ThemeContext } from 'app-themes';
import { GoogleAddress, ReferenceField } from 'app-types';
import { find, isEmpty } from 'lodash-es';

interface Props {
	userStore?: UserStore;
	addressesStore?: AddressesStore;
	bookingDictionariesStore?: BookingDictionariesStore;
	ccStore?: CCStore;
	bookingCCStore?: BookingCCStore;
}

@inject(
	({
		userStore,
		bookingDictionariesStore,
		addressesStore,
		ccStore,
		bookingCCStore,
	}: RootStore) => ({
		userStore,
		bookingDictionariesStore,
		addressesStore,
		ccStore,
		bookingCCStore,
	})
)
@observer
class UserProfileForm extends Component<Props & FormComponentProps> {
	static contextType = ThemeContext;

	componentDidMount() {
		// To disabled submit button at the beginning.
		this.props.form.validateFieldsAndScroll();
	}

	componentWillUnmount(): void {
		const { clearAllAddressesData } = this.props.addressesStore!;
		this.props.form.resetFields();
		clearAllAddressesData();
	}

	getReferenceFields() {
		let referenceFields: ReferenceField[] = [];
		const { userInfo, customerInfoData } = this.props.userStore!;

		if (!userInfo || !customerInfoData) {
			return referenceFields;
		}

		const { referenceFields: referenceFieldsValue } = userInfo;
		const { referenceFields: referenceFieldsName } = customerInfoData;

		referenceFields = referenceFieldsName.map(item => {
			const nameData = find(
				referenceFieldsValue,
				name => name.customerReferenceFieldId === item.id
			);

			return {
				customerReferenceFieldId: Number(item.id),
				referenceFieldName: item.name,
				value: (nameData && nameData.defaultValue) || '',
			};
		});

		return referenceFields;
	}

	render() {
		const {
			receiveGoogleAddresses,
			validateGoogleAddress,
			addressesSuggestions,
			validationAddressErrors,
			clearAllAddressesData,
			isAddressLoading,
			isValidateAddressLoading,
		} = this.props.addressesStore!;
		const { userInfo } = this.props.userStore!;
		const { countriesDictionary, carTypesDictionary } = this.props.bookingDictionariesStore!;
		const {
			activeCreditCards,
			deletedCreditCards,
			expiredCreditCards,
			saveCreditCard,
			updateActiveCreditCards,
			dndItems,
			fetchStatus,
		} = this.props.ccStore!;
		const { isCreditCardUser } = this.props.bookingCCStore!;
		const { getFieldDecorator } = this.props.form;

		if (userInfo === null) {
			return userInfo;
		}

		const referenceFields = this.getReferenceFields();

		return (
			<Form layout="vertical" className={styles.container}>
				<h2>Personal Details</h2>
				<div className="modalBox modalBoxBooking">
					<div className="modalBoxRow">
						<div className="modalBoxCol w6">
							<Form.Item label="First name" validateStatus={''} help={false}>
								{getFieldDecorator(FORM_STRUCTURE.FIRST_NAME, {
									initialValue: userInfo.firstName,
									rules: [
										{
											required: true,
											whitespace: true,
										},
									],
								})(<Input />)}
							</Form.Item>
						</div>
						<div className="modalBoxCol w6">
							<Form.Item label="Last name" validateStatus={''} help={false}>
								{getFieldDecorator(FORM_STRUCTURE.LAST_NAME, {
									initialValue: userInfo.lastName,
									rules: [
										{
											required: true,
											whitespace: true,
										},
									],
								})(<Input />)}
							</Form.Item>
						</div>
					</div>
					<div className="modalBoxRow">
						<div className="modalBoxCol w6">
							<Form.Item label="Phone" validateStatus={''} help={true}>
								<PhoneInput
									form={this.props.form}
									initialValue={{
										phoneNumber: userInfo.phoneNumber,
										phoneCodeCountryId: userInfo.phoneCodeCountryId,
									}}
									countries={toJS(countriesDictionary)}
								/>
							</Form.Item>
						</div>
						<div className="modalBoxCol w6">
							<Form.Item label="Email address" validateStatus={''} help={false}>
								{getFieldDecorator(FORM_STRUCTURE.EMAIL_ADDRESS, {
									initialValue: userInfo.emailAddress,
									rules: [
										{
											required: true,
											whitespace: true,
										},
									],
								})(<Input />)}
							</Form.Item>
						</div>
					</div>
					<div className="modalBoxRow">
						<div className="modalBoxCol w12">
							<NotificationByEmail
								form={this.props.form}
								bookingConfirmationName={FORM_STRUCTURE.BOOKING_CONFIRMATION}
								bookingConfirmationData={userInfo.bookingConfirmation}
								invoiceConfirmationName={FORM_STRUCTURE.INVOICE_CONFIRMATION}
								invoiceConfirmationData={userInfo.invoiceConfirmation}
								calendarBookingEventName={FORM_STRUCTURE.CALENDAR_BOOKING_EVENT}
								calendarBookingEventData={userInfo.calendarBookingEvent}
							/>
						</div>
					</div>
					<div className="modalBoxRow">
						<div className="modalBoxCol w12">
							<Form.Item
								label={getOptionalLabel('Home address')}
								validateStatus={''}
								help={false}
							>
								<LocationInput
									initialValue={
										userInfo.placeId
											? {
													placeId: userInfo.placeId,
													fullAddress: userInfo.formattedAddress,
													name: userInfo.addressName,
											  }
											: undefined
									}
									form={this.props.form}
									onStringAddressChange={(searchText: string) =>
										receiveGoogleAddresses(searchText, {
											label: FORM_STRUCTURE.USER_ADDRESS_PLACE_ID,
										})
									}
									validateGoogleAddress={(placeId: string) =>
										validateGoogleAddress(
											placeId,
											FORM_STRUCTURE.USER_ADDRESS_PLACE_ID,
											false,
											true
										)
									}
									onClear={clearAllAddressesData}
									fieldName={FORM_STRUCTURE.USER_ADDRESS_PLACE_ID}
									addressesSuggestions={
										addressesSuggestions[
											FORM_STRUCTURE.USER_ADDRESS_PLACE_ID
										] as GoogleAddress[]
									}
									validationAddressErrors={
										validationAddressErrors[
											FORM_STRUCTURE.USER_ADDRESS_PLACE_ID
										]
									}
									isAddressLoading={isAddressLoading}
									isValidateAddressLoading={isValidateAddressLoading(
										FORM_STRUCTURE.USER_ADDRESS_PLACE_ID
									)}
								/>
							</Form.Item>
						</div>
					</div>
					<div className="modalBoxRow">
						<div className="modalBoxCol w6">
							<Form.Item label="User name" validateStatus={''} help={false}>
								<span className={styles.readOnlyField}>{userInfo.userName}</span>
							</Form.Item>
						</div>
						<div className="modalBoxCol w6">
							<Form.Item label="Password" validateStatus={''} help={false}>
								{getFieldDecorator(FORM_STRUCTURE.PASSWORD, {
									initialValue: '',
									rules: [
										{
											required: false,
											whitespace: true,
										},
									],
								})(<InputPassword showHint />)}
							</Form.Item>
						</div>
					</div>
				</div>

				{!isEmpty(referenceFields) && (
					<>
						<h2>Reference Fields</h2>
						<div className="modalBoxBooking modalBox">
							<ReferenceFields
								form={this.props.form}
								referenceFields={referenceFields}
							/>
						</div>
					</>
				)}

				<h2>{getOptionalLabel('Trip details')}</h2>
				<div className="modalBox modalBoxBooking">
					<div className="modalBoxRow">
						<div className="modalBoxCol w6" id={'preferred-service-type'}>
							<Form.Item
								label="Preferred service type"
								validateStatus={''}
								help={false}
							>
								{getFieldDecorator(FORM_STRUCTURE.PREFERRED_SERVICE_TYPE, {
									initialValue: Number(userInfo.preferredCarTypeId) || '',
									rules: [
										{
											required: false,
										},
									],
								})(
									<Select
										showSearch
										allowClear
										getPopupContainer={() =>
											document.getElementById('preferred-service-type') ||
											document.body
										}
										optionFilterProp="children"
										filterOption={(input: any, option: any) =>
											option.props.children
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									>
										{carTypesDictionary.map(item => (
											<Select.Option
												key={item.carTypeId}
												value={item.carTypeId}
											>
												{item.carTypeName}
											</Select.Option>
										))}
									</Select>
								)}
							</Form.Item>
						</div>
					</div>
					<div className="modalBoxRow">
						<div className="modalBoxCol w12">
							<Form.Item label="Notes to driver" validateStatus={''} help={false}>
								{getFieldDecorator(FORM_STRUCTURE.JOURNEY_NOTES, {
									initialValue: userInfo.journeyNotes,
								})(<TextArea rows={6} />)}
							</Form.Item>
						</div>
					</div>
				</div>

				<h2>{getOptionalLabel('Alternative contacts')}</h2>
				<div className="modalBoxBooking modalBox">
					<AlternativeContacts
						maxRows={2}
						form={this.props.form}
						countriesDictionary={countriesDictionary}
						initialValues={userInfo.alternativeContactList}
						isNotificationByEmailVisible
					/>
				</div>

				{isCreditCardUser && (
					<>
						<h2>{getOptionalLabel('Credit Cards')}</h2>
						<div className="modalBoxBooking modalBox">
							<CreditCards
								dndItems={dndItems}
								fetchStatus={fetchStatus}
								activeCreditCards={activeCreditCards}
								deletedCreditCards={deletedCreditCards}
								expiredCreditCards={expiredCreditCards}
								saveCreditCard={saveCreditCard}
								updateActiveCreditCards={updateActiveCreditCards}
							/>
						</div>
					</>
				)}
			</Form>
		);
	}
}

export default UserProfileForm;
